import React from 'react';
import { Link } from 'react-router-dom';
import {
    Settings,
    Pencil,
    CheckCircle2,
    Columns3,
    LayoutGrid,
    Gamepad2,
    MinusCircle,
    Highlighter
} from 'lucide-react';
import ThreeBrainIcon from '../components/ThreeBrainIcon';
import ThoughtBubbleIcon from '../components/ThoughtBubbleIcon';
// import './LandingPage.css';

// Load Font via simple style injection for this page specifically
// Load Styles (Fonts + Bubbles + Neon)
const StyleLoader = () => (
    <style>
        {`
            @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@700;800&display=swap');
            .font-rounded { font-family: 'Nunito', sans-serif; }

            /* Custom Neon Glow for Brain Break */
            .neon-text-glow { text-shadow: 0 0 10px rgba(34, 211, 238, 0.6); }
            .neon-border-glow { box-shadow: 0 0 15px rgba(59, 130, 246, 0.5); }

            /* Water Bubbles Animation */
            .bubbles-container { position: absolute; inset: 0; overflow: hidden; pointer-events: none; z-index: 0; }
            .bubble {
                position: absolute;
                bottom: -100px;
                background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.1));
                border-radius: 50%;
                box-shadow: 0 4px 15px rgba(255,255,255,0.2);
                border: 1px solid rgba(255, 255, 255, 0.3);
                animation: rise infinite linear;
            }
            @keyframes rise {
                0% { transform: translateY(0) translateX(0); opacity: 0; }
                10% { opacity: 1; }
                90% { opacity: 1; }
                100% { transform: translateY(-120vh) translateX(20px); opacity: 0; }
            }
        `}
    </style>
);

const Bubbles = () => {
    // Generate static bubbles to avoid hydration mismatch if possible, or use simple mapping
    const bubbles = Array.from({ length: 15 }).map((_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        size: `${Math.random() * 40 + 20}px`, // 20px to 60px
        duration: `${Math.random() * 10 + 10}s`, // 10s to 20s
        delay: `${Math.random() * 5}s`
    }));

    return (
        <div className="bubbles-container">
            {bubbles.map(b => (
                <div
                    key={b.id}
                    className="bubble"
                    style={{
                        left: b.left,
                        width: b.size,
                        height: b.size,
                        animationDuration: b.duration,
                        animationDelay: b.delay
                    }}
                />
            ))}
        </div>
    );
};

const AppCard = ({ to, icon: Icon, title, iconBoxColor, cardColorClass, isDark, iconColor = "text-white" }) => {
    // Determine card background and border
    // Default or passed color class
    let cardClasses = cardColorClass || "bg-white border border-transparent shadow-md hover:shadow-2xl hover:shadow-blue-500/10";

    // Specific overrides
    if (isDark) cardClasses = "bg-slate-900 border border-blue-400/30 shadow-md hover:shadow-cyan-500/30 neon-border-glow";

    return (
        <Link
            to={to}
            className={`
                flex flex-col items-center justify-center 
                aspect-square p-5
                rounded-xl 
                transition-all duration-200 ease-in-out
                hover:-translate-y-1 relative z-10
                ${cardClasses}
            `}
        >
            {/* Icon Container (Filled Box) */}
            <div className={`
                w-14 h-14 rounded-2xl flex items-center justify-center mb-4 shadow-sm
                transition-transform duration-300 group-hover:scale-105
                ${iconBoxColor}
                ${isDark ? 'shadow-[0_0_15px_rgba(34,211,238,0.4)]' : ''}
            `}>
                <Icon
                    size={28}
                    className={iconColor}
                    strokeWidth={2.5}
                />
            </div>

            {/* Title */}
            <span className={`
                text-sm font-bold tracking-tight
                ${isDark ? 'text-white neon-text-glow' : 'text-slate-700'}
            `}>
                {title}
            </span>
        </Link>
    );
};

function LandingPage() {
    return (
        <div className="min-h-screen bg-gradient-to-br from-cyan-400 to-blue-600 flex flex-col font-sans relative overflow-hidden">
            <StyleLoader />
            <Bubbles />

            {/* 1. Header: Restore the Vibe */}
            <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md shadow-sm px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-blue-600">
                    <ThoughtBubbleIcon className="fill-blue-600" size={28} />
                    <span className="text-2xl font-extrabold font-rounded tracking-tight">
                        Thoughtblink
                    </span>
                </div>


            </header>

            {/* 2. Main Content Grid */}
            <main className="flex-grow p-6 relative z-10">

                {/* Hero Text */}
                <div className="flex items-center justify-center gap-3 mb-10 mt-4">
                    <span className="text-3xl font-semibold text-white drop-shadow-md tracking-tight">
                        What's on your mind?
                    </span>
                    <div className="w-20 h-20 -my-6 relative z-0 pointer-events-none filter drop-shadow-lg">
                        <ThreeBrainIcon />
                    </div>
                </div>

                <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">

                    {/* Hero: Sticky Ideas */}
                    <AppCard
                        to="/sticky-notes"
                        icon={Pencil}
                        title="Sticky Ideas"
                        cardColorClass="bg-yellow-50 border border-yellow-200 shadow-md hover:shadow-xl hover:shadow-yellow-500/10"
                        iconBoxColor="bg-yellow-500" // Deep Gold
                    />

                    {/* To Do */}
                    <AppCard
                        to="/to-do"
                        icon={CheckCircle2}
                        title="To Do"
                        cardColorClass="bg-green-50 border border-green-200 shadow-md hover:shadow-xl hover:shadow-green-500/10"
                        iconBoxColor="bg-green-500"
                    />

                    {/* Kanban-It */}
                    <AppCard
                        to="/kanban-it"
                        icon={Columns3}
                        title="Kanban-It"
                        cardColorClass="bg-blue-50 border border-blue-200 shadow-md hover:shadow-xl hover:shadow-blue-500/10"
                        iconBoxColor="bg-blue-500"
                    />

                    {/* Prioritize-It */}
                    <AppCard
                        to="/prioritize-it"
                        icon={LayoutGrid}
                        title="Prioritize-It"
                        cardColorClass="bg-orange-50 border border-orange-200 shadow-md hover:shadow-xl hover:shadow-orange-500/10"
                        iconBoxColor="bg-orange-500"
                    />

                    {/* Whiteboard-It */}
                    <AppCard
                        to="/whiteboard-it"
                        icon={Highlighter}
                        title="Whiteboard-It"
                        cardColorClass="bg-purple-50 border border-purple-200 shadow-md hover:shadow-xl hover:shadow-purple-500/10"
                        iconBoxColor="bg-purple-500"
                    />

                    {/* Special Case: Brain Break */}
                    <AppCard
                        to="/brain-break"
                        icon={Gamepad2}
                        title="Brain Break"
                        isDark={true}
                        iconBoxColor="bg-slate-800"
                        iconColor="text-cyan-400"
                    />

                </div>
            </main>
        </div>
    );
}

export default LandingPage;
